// design-system-exempt: the default accent below is the stored DEFAULT VALUE of a
// workspace's brand, which is data. global.css mirrors it as --ember.
// What a deployment calls itself and what it looks like.
//
// This is data, not configuration: it lives in the log's config table and is
// edited in Settings, so a team changing its accent colour is not a redeploy.
// Only secrets and the identity provider are env.
//
// It is kept separate from `Config` in model.ts on purpose. `Config` is the
// pipeline — every member edits it as part of using the product. The brand is
// what the deployment IS, so it is admin-only, and a separate record makes that
// a separate endpoint rather than a per-field check.

export interface Brand {
  /** What this deployment is called, in the sidebar and the page title.
   *  Empty means first run, which is what sends an admin to /setup. */
  instanceName: string
  /** Blob key for the logo, served from /blob/<key>. Empty for none. */
  logoKey: string
  /** Accent colour as a hex string; everything else derives from the palette. */
  accent: string
  /** One line under the sign-in button. Somewhere to say "staff only". */
  loginBlurb: string
}

export const DEFAULT_BRAND: Brand = {
  instanceName: '',
  // Ember: the accent the landing page, the demo and the product's own chrome use.
  // The previous default was a blue that appears nowhere else in crum.
  accent: '#a8501f',
  logoKey: '',
  loginBlurb: '',
}

/** Merge a stored brand over the defaults, dropping anything of the wrong type.
 *  Config read from storage is never trusted to be the current shape. */
export function normalizeBrand(raw: unknown): Brand {
  const b = (raw ?? {}) as Partial<Record<keyof Brand, unknown>>
  const str = (v: unknown, fallback: string) => (typeof v === 'string' ? v : fallback)
  return {
    instanceName: str(b.instanceName, DEFAULT_BRAND.instanceName).slice(0, 80),
    logoKey: str(b.logoKey, DEFAULT_BRAND.logoKey),
    accent: isHex(b.accent) ? (b.accent as string) : DEFAULT_BRAND.accent,
    loginBlurb: str(b.loginBlurb, DEFAULT_BRAND.loginBlurb).slice(0, 200),
  }
}

function isHex(v: unknown): boolean {
  return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v)
}

/** The product name when a deployment hasn't named itself yet. */
export const FALLBACK_NAME = 'Crum'

export function displayName(brand: Brand): string {
  return brand.instanceName.trim() || FALLBACK_NAME
}
